import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Plus, Calendar, CheckCircle, Eye } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Button } from '../components/ui/button';

const Maintenance = () => {
  const [maintenance, setMaintenance] = useState([]);
  const [machinery, setMachinery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    machinery_id: '',
    type: 'preventivo',
    description: '',
    scheduled_date: '',
    cost: 0,
    status: 'pending'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [maintenanceRes, machineryRes] = await Promise.all([
        axios.get(`${API}/maintenance`),
        axios.get(`${API}/machinery`)
      ]);
      setMaintenance(maintenanceRes.data);
      setMachinery(machineryRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/maintenance`, formData);
      toast.success('Mantenimiento creado');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleComplete = async (id) => {
    try {
      await axios.patch(`${API}/maintenance/${id}/complete`);
      toast.success('Mantenimiento completado');
      fetchData();
    } catch (error) {
      toast.error('Error al completar');
    }
  };

  const resetForm = () => {
    setFormData({
      machinery_id: '',
      type: 'preventivo',
      description: '',
      scheduled_date: '',
      cost: 0,
      status: 'pending'
    });
  };

  const getMachineryName = (machineryId) => {
    const m = machinery.find(item => item.id === machineryId);
    return m ? m.name : 'Desconocido';
  };

  return (
    <div data-testid="maintenance-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Mantenimiento
          </h1>
          <p className="text-slate-600 mt-2">Gestión de mantenimientos preventivos y correctivos</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-maintenance-button" className="btn-primary">
              <Plus size={20} className="mr-2" />
              Programar Mantenimiento
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                Nuevo Mantenimiento
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} data-testid="maintenance-form" className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Maquinaria</label>
                <select
                  data-testid="machinery-select"
                  value={formData.machinery_id}
                  onChange={(e) => setFormData({ ...formData, machinery_id: e.target.value })}
                  className="input-field"
                  required
                >
                  <option value="">Seleccionar...</option>
                  {machinery.map((m) => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Tipo</label>
                <select
                  data-testid="type-select"
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  className="input-field"
                >
                  <option value="preventivo">Preventivo</option>
                  <option value="correctivo">Correctivo</option>
                  <option value="inspeccion">Inspección</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Descripción</label>
                <textarea
                  data-testid="description-input"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="input-field"
                  rows={3}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Fecha Programada</label>
                <input
                  type="date"
                  data-testid="date-input"
                  value={formData.scheduled_date}
                  onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                  className="input-field"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Costo Estimado</label>
                <input
                  type="number"
                  step="0.01"
                  data-testid="cost-input"
                  value={formData.cost}
                  onChange={(e) => setFormData({ ...formData, cost: parseFloat(e.target.value) })}
                  className="input-field"
                />
              </div>
              <div className="flex justify-end space-x-3">
                <Button
                  type="button"
                  data-testid="cancel-button"
                  onClick={() => { setDialogOpen(false); resetForm(); }}
                  className="btn-secondary"
                >
                  Cancelar
                </Button>
                <Button type="submit" data-testid="save-button" className="btn-primary">
                  Crear
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-center py-12">Cargando...</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="maintenance-grid">
          {maintenance.map((m) => (
            <div key={m.id} className="bg-white border border-slate-200 rounded-md p-6" data-testid={`maintenance-card-${m.id}`}>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-slate-800" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                    {getMachineryName(m.machinery_id)}
                  </h3>
                  <p className="text-sm text-slate-600">{m.type}</p>
                </div>
                <span className={`status-badge status-${m.status}`}>
                  {m.status === 'pending' ? 'Pendiente' : 'Completado'}
                </span>
              </div>
              <p className="text-slate-800 mb-4">{m.description}</p>
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center text-slate-600">
                  <Calendar size={16} className="mr-2" />
                  {new Date(m.scheduled_date).toLocaleDateString()}
                </div>
                <div className="mono font-medium">Bs. {m.cost.toFixed(2)}</div>
              </div>
              <div className="space-y-2">
                <Link to={`/maintenance/${m.id}`}>
                  <button
                    className="w-full btn-secondary flex items-center justify-center"
                    data-testid={`view-button-${m.id}`}
                  >
                    <Eye size={18} className="mr-2" />
                    Ver Detalle
                  </button>
                </Link>
                {m.status === 'pending' && (
                  <button
                    data-testid={`complete-button-${m.id}`}
                    onClick={() => handleComplete(m.id)}
                    className="w-full btn-primary flex items-center justify-center"
                  >
                    <CheckCircle size={18} className="mr-2" />
                    Completar
                  </button>
                )}
              </div>
            </div>
          ))}
          {maintenance.length === 0 && (
            <div className="col-span-2 text-center py-12 text-slate-500">
              No hay mantenimientos programados
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Maintenance;